/**
 * The blocks of GET /config the Studio reads beyond the kit's: the org list
 * page cap and the knowledge-base categories. The kit loads /config once and
 * keeps keys it does not name; these schemas read them from the same response.
 * A block that is missing or malformed throws, so the calling query shows its
 * error with a retry instead of running on a guessed value.
 */
import { useConfigStore, type PlatformConfig } from '@ai4nr/tricorder-ui'
import { z } from 'zod'

const OrgBlockSchema = z.object({ org: z.object({ max_page_size: z.number().int().positive() }) })

const KnowledgeBaseBlockSchema = z.object({
  knowledge_base: z
    .object({
      categories: z.array(z.object({ key: z.string(), label_en: z.string(), label_de: z.string() })).optional(),
    })
    .optional(),
})

export type KnowledgeBaseCategory = NonNullable<
  NonNullable<z.infer<typeof KnowledgeBaseBlockSchema>['knowledge_base']>['categories']
>[number]

function loaded(): PlatformConfig {
  const config = useConfigStore.getState().config
  if (!config) throw new Error('/config has not loaded; gate this call on isConfigLoaded')
  return config
}

/** app.yaml `org.max_page_size`: the cap the org list endpoints clamp `limit` to. */
export function orgPageSize(): number {
  return OrgBlockSchema.parse(loaded()).org.max_page_size
}

const LocationKindsSchema = z.object({ org: z.object({ location_kinds: z.array(z.string()) }) })

/**
 * app.yaml `org.location_kinds`: the kinds a new org location may take. Read
 * during render, so a /config without the block answers no kinds, which leaves
 * "create location" disabled, rather than throwing out of the screen.
 */
export function locationKinds(config: PlatformConfig | null): string[] {
  const parsed = LocationKindsSchema.safeParse(config)
  return parsed.success ? parsed.data.org.location_kinds : []
}

/**
 * The knowledge-base categories /config declares; a /config that declares none
 * is an empty list. A /config that has not loaded throws: the caller gates on it.
 */
export function knowledgeBaseCategories(config: PlatformConfig | null): KnowledgeBaseCategory[] {
  if (!config) throw new Error('/config has not loaded; gate this call on isConfigLoaded')
  return KnowledgeBaseBlockSchema.parse(config).knowledge_base?.categories ?? []
}
